import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, BadgeCheck, Car, Flag, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Stat = ({ label, value }) => (
  <div className="t-card p-4">
    <p className="font-display font-black text-2xl">{value}</p>
    <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
  </div>
);

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [reports, setReports] = useState([]);
  const [rides, setRides] = useState([]);
  const [viewDoc, setViewDoc] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get("/admin/verifications").then(({ data }) => setVerifications(data)).catch(() => {});
    api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
    api.get("/admin/rides").then(({ data }) => setRides(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (verId, action) => {
    try {
      await api.post(`/admin/verifications/${verId}/review`, { action });
      toast.success(action === "approve" ? "Member verified" : "Verification rejected");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reportAction = async (repId, action) => {
    try {
      await api.post(`/admin/reports/${repId}/action`, { action });
      toast.success(`Report ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!overview) return <p className="text-muted-foreground animate-pulse text-center py-10">Loading admin…</p>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <h1 className="font-display font-black text-3xl flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-primary" /> Institution Admin</h1>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Members" value={overview.members} />
        <Stat label="Verified" value={overview.verified} />
        <Stat label="Active rides" value={overview.active_rides} />
        <Stat label="Open reports" value={overview.open_reports} />
      </div>

      <Tabs defaultValue="verifications">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-verifications" value="verifications" className="rounded-full">
            Verifications {overview.pending_verifications > 0 && <span className="ml-1 bg-primary text-white rounded-full px-1.5 text-xs">{overview.pending_verifications}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-reports" value="reports" className="rounded-full">Reports</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-members" value="members" className="rounded-full">Members</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-rides" value="rides" className="rounded-full">Rides</TabsTrigger>
        </TabsList>

        <TabsContent value="verifications" className="mt-6 space-y-3">
          {verifications.length ? verifications.map((v) => (
            <div key={v.id} className="t-card p-5" data-testid={`verification-${v.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{v.user?.name} <span className="font-normal text-muted-foreground">· {v.user?.email}</span></p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Claimed: {v.name_on_id} · ID {v.id_number} · OCR confidence {Math.round((v.confidence || 0) * 100)}%
                  </p>
                  {v.ocr_result && (
                    <p className="text-xs text-muted-foreground">
                      OCR read: {v.ocr_result.name || "—"} · {v.ocr_result.institution || "—"} · {v.ocr_result.id_number || "—"}
                    </p>
                  )}
                </div>
                <button data-testid={`view-doc-${v.id}`} onClick={() => setViewDoc(v.id)} className="text-sm font-bold text-primary">View ID</button>
                <Button data-testid={`approve-ver-${v.id}`} size="sm" onClick={() => review(v.id, "approve")} className="rounded-full font-bold">
                  <BadgeCheck className="w-4 h-4 mr-1" /> Approve
                </Button>
                <Button data-testid={`reject-ver-${v.id}`} size="sm" variant="outline" onClick={() => review(v.id, "reject")} className="rounded-full font-bold text-destructive border-destructive/30">Reject</Button>
              </div>
              {viewDoc === v.id && <DocViewer verId={v.id} onClose={() => setViewDoc(null)} />}
            </div>
          )) : <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-pending-verifications">No verifications waiting for review.</div>}
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-3">
          {reports.length ? reports.map((r) => (
            <div key={r.id} className="t-card p-5" data-testid={`report-${r.id}`}>
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-destructive" />
                <span className="t-chip bg-destructive/10 text-destructive">{r.category.replace("_", " ")}</span>
                <span className="text-xs text-muted-foreground">on {r.target_type}</span>
              </div>
              {r.target_preview && <p className="text-sm mt-2 italic">"{r.target_preview}"</p>}
              {r.details && <p className="text-xs text-muted-foreground mt-1">Reporter says: {r.details}</p>}
              {r.anonymous_author && <p className="text-xs mt-1 font-bold text-[#6C5CE7]">Anonymous post — actual author (admin only): {r.anonymous_author.name}</p>}
              <div className="flex gap-2 mt-3">
                <Button data-testid={`report-hide-${r.id}`} size="sm" variant="outline" onClick={() => reportAction(r.id, "hide")} className="rounded-full font-bold">Hide</Button>
                <Button data-testid={`report-remove-${r.id}`} size="sm" variant="outline" onClick={() => reportAction(r.id, "remove")} className="rounded-full font-bold text-destructive border-destructive/30">Remove</Button>
                <Button data-testid={`report-dismiss-${r.id}`} size="sm" variant="ghost" onClick={() => reportAction(r.id, "dismiss")} className="rounded-full font-bold">Dismiss</Button>
              </div>
            </div>
          )) : <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-reports">No open reports. Campus is peaceful.</div>}
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          {members.length ? (
            <div className="t-card divide-y divide-black/5">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-4" data-testid={`member-${m.id}`}>
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  </div>
                  <span className={`t-chip ${m.verification_status === "verified" ? "bg-secondary/20 text-[#14532D]" : "bg-[#F9C31F] text-[#12211C]"}`}>{m.verification_status}</span>
                </div>
              ))}
            </div>
          ) : <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-members">No members registered yet.</div>}
        </TabsContent>

        <TabsContent value="rides" className="mt-6">
          {rides.length ? (
            <div className="t-card divide-y divide-black/5">
              {rides.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-4" data-testid={`admin-ride-${r.id}`}>
                  <Car className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{r.from_location} → {r.to_location}</p>
                    <p className="text-xs text-muted-foreground">{r.driver_name} · {r.date} {r.departure_time} · ₹{r.contribution}</p>
                  </div>
                  <span className="t-chip bg-black/5">{r.status}</span>
                </div>
              ))}
            </div>
          ) : <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-admin-rides">No rides created yet.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DocViewer({ verId, onClose }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api.get(`/admin/verifications/${verId}/document`).then(({ data }) => setDoc(data.document)).catch(() => setDoc(false));
  }, [verId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader><DialogTitle>ID Document (confidential)</DialogTitle></DialogHeader>
        {doc === null ? <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>
          : doc === false ? <p className="text-sm text-destructive">Could not load document.</p>
          : <img src={`data:image/jpeg;base64,${doc}`} alt="ID document" className="rounded-2xl w-full" data-testid="id-document-img" />}
      </DialogContent>
    </Dialog>
  );
}
